import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { messagingApi } from '@/api/ops';
import type { SenderId } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { ConfirmDialog, Modal } from './Modal';
import { MutationError } from './Problem';
import { StatusChip } from './Badges';
import { CheckboxField, ReasonField, TextAreaField, TextField, reasonOk, splitList } from './Forms';
import { formatTimestamp, shortId } from '@/ops/format';

type Transition = 'submit' | 'approve' | 'reject' | 'suspend';

/** The registry workflow (D-CP20): which transitions each status allows. */
const TRANSITIONS: Record<string, Transition[]> = {
  draft: ['submit'],
  submitted: ['approve', 'reject'],
  approved: ['suspend'],
  rejected: ['submit'],
  // messaging re-approves a suspended id directly (submit is only from draft or rejected — a 409 otherwise).
  suspended: ['approve'],
};

export const senderIdsKey = (env: string) => ['ops', 'messaging', env, 'sender-ids'] as const;

function parseDocuments(text: string): { ok: true; value: unknown } | { ok: false; error: string } {
  if (!text.trim()) return { ok: true, value: undefined };
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch {
    return { ok: false, error: 'Documents must be valid JSON (e.g. {"commercial_registration": "…"}).' };
  }
}

/**
 * The sender-id registry with its approval workflow. Registration is a platform↔regulator process
 * (~15 business days in Egypt), so the dates and the documents submitted are part of the row.
 */
export function SenderIdsTable({ env, senderIds, showTenant = true }: { env: string; senderIds: SenderId[]; showTenant?: boolean }) {
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.messagingManage);
  const [pending, setPending] = useState<{ row: SenderId; transition: Transition } | null>(null);
  const [reason, setReason] = useState('');
  const [documents, setDocuments] = useState('');

  const docs = parseDocuments(documents);
  const needsReason = pending?.transition === 'reject' || pending?.transition === 'suspend';

  const transition = useMutation({
    mutationFn: () =>
      messagingApi.transitionSenderId(env, pending!.row.id, pending!.transition, {
        reason: reason.trim() || undefined,
        documents: pending!.transition === 'submit' && docs.ok ? docs.value : undefined,
      }),
    onSuccess: () => {
      setPending(null);
      setReason('');
      setDocuments('');
      queryClient.invalidateQueries({ queryKey: senderIdsKey(env) });
    },
  });

  if (senderIds.length === 0) return <div className="empty-state t-body">No sender ids.</div>;

  return (
    <>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Sender id</th>
              {showTenant && <th>Tenant</th>}
              <th>Status</th>
              <th>Networks · countries</th>
              <th>Dates</th>
              <th>Documents</th>
              {canManage && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {senderIds.map((s) => (
              <tr key={s.id}>
                <td>
                  <span className="mono" style={{ fontWeight: 600 }}>{s.value}</span>
                  {s.is_default && <span className="chip chip-info" style={{ marginInlineStart: 6 }}>default</span>}
                  <div className="t-caption muted">{s.provider_key ?? 'any provider'}</div>
                </td>
                {showTenant && <td className="t-caption mono" title={s.tenant_id}>{shortId(s.tenant_id)}</td>}
                <td>
                  <StatusChip status={s.registration_status} />
                  {/* A resubmitted id keeps its old rejection note in messaging: show each note only in its own status. */}
                  {s.registration_status === 'rejected' && s.rejected_reason && <div className="t-caption note-error">rejected: {s.rejected_reason}</div>}
                  {s.registration_status === 'suspended' && s.suspended_reason && <div className="t-caption note-error">suspended: {s.suspended_reason}</div>}
                </td>
                <td className="t-caption">
                  {s.networks.join(', ') || 'all networks'}
                  <div className="muted">{s.countries.join(', ') || 'any country'}</div>
                </td>
                <td className="t-caption muted">
                  {s.submitted_at && <div>submitted {formatTimestamp(s.submitted_at)}</div>}
                  {s.approved_at && <div>approved {formatTimestamp(s.approved_at)}</div>}
                  {s.rejected_at && <div>rejected {formatTimestamp(s.rejected_at)}</div>}
                  {s.suspended_at && <div>suspended {formatTimestamp(s.suspended_at)}</div>}
                  {!s.submitted_at && !s.approved_at && <div>created {formatTimestamp(s.created_at)}</div>}
                </td>
                <td className="t-caption">
                  {s.documents && typeof s.documents === 'object' && Object.keys(s.documents as object).length > 0 ? (
                    <details>
                      <summary>{Object.keys(s.documents as object).length} item(s)</summary>
                      <pre className="pre-json">{JSON.stringify(s.documents, null, 2)}</pre>
                    </details>
                  ) : (
                    <span className="subtle">none</span>
                  )}
                </td>
                {canManage && (
                  <td>
                    <div className="row">
                      {(TRANSITIONS[s.registration_status] ?? []).map((t) => (
                        <button
                          key={t}
                          type="button"
                          className={`btn btn-ghost btn-sm${t === 'reject' || t === 'suspend' ? ' is-danger' : ''}`}
                          onClick={() => setPending({ row: s, transition: t })}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pending && (
        <ConfirmDialog
          title={`${pending.transition} sender id “${pending.row.value}”`}
          confirmLabel={pending.transition}
          destructive={needsReason}
          pending={transition.isPending}
          confirmDisabled={(needsReason && !reasonOk(reason)) || !docs.ok}
          onConfirm={() => transition.mutate()}
          onCancel={() => {
            setPending(null);
            setReason('');
            setDocuments('');
            transition.reset();
          }}
        >
          {pending.transition === 'approve' && (
            <p className="t-body" style={{ margin: 0 }}>
              An approved sender id can carry marketing templates. Approve only once the operator/regulator
              registration is confirmed.
            </p>
          )}
          {pending.transition === 'submit' && (
            <TextAreaField label="Documents submitted (JSON, optional)" value={documents} onChange={setDocuments} mono
              rows={4} error={docs.ok ? null : docs.error} />
          )}
          <ReasonField
            value={reason}
            onChange={setReason}
            label={needsReason ? 'Reason (required)' : 'Note (optional)'}
            showError={needsReason && reason.length > 0}
          />
          <MutationError error={transition.error} service="messaging" />
        </ConfirmDialog>
      )}
    </>
  );
}

/** Console-side registration of a tenant's sender id. */
export function CreateSenderIdForm({ env, tenantId, onClose }: { env: string; tenantId?: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [tenant, setTenant] = useState(tenantId ?? '');
  const [value, setValue] = useState('');
  const [networks, setNetworks] = useState('');
  const [countries, setCountries] = useState('EG');
  const [documents, setDocuments] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [providerKey, setProviderKey] = useState('');

  const docs = parseDocuments(documents);
  const tenantValid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(tenant.trim());
  // messaging silently replaces a list it cannot read with ["EG"] (countries) or [] (networks) — so the form refuses one.
  const countryList = splitList(countries).map((c) => c.toUpperCase());
  const countriesValid = countryList.every((c) => /^[A-Z]{2}$/.test(c));
  const valid = tenantValid && value.trim().length > 0 && value.trim().length <= 11 && docs.ok && countriesValid;

  const create = useMutation({
    mutationFn: () =>
      messagingApi.createSenderId(env, {
        tenant_id: tenant.trim(),
        value: value.trim(),
        networks: splitList(networks),
        countries: countryList,
        documents: docs.ok ? docs.value : undefined,
        is_default: isDefault,
        provider_key: providerKey.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderIdsKey(env) });
      onClose();
    },
  });

  return (
    <Modal
      title="Register a sender id"
      onClose={create.isPending ? undefined : onClose}
      dismissible={!create.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={create.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Saving…' : 'Register (draft)'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <TextField label="Tenant id" value={tenant} onChange={setTenant} mono required disabled={!!tenantId}
          error={tenant && !tenantValid ? 'A tenant UUID.' : null} />
        <TextField label="Sender id" value={value} onChange={setValue} required mono hint="Up to 11 characters; generic ids (INFO, SMS) are refused by operators." />
        <TextField label="Networks" value={networks} onChange={setNetworks} hint="Comma separated; empty = all." />
        <TextField label="Countries" value={countries} onChange={setCountries} hint="ISO-2, comma separated (EG, SA, AE)."
          error={countriesValid ? null : 'Two-letter country codes only, e.g. EG, SA, AE.'} />
        <TextField label="Provider key" value={providerKey} onChange={setProviderKey} mono hint="Empty = any provider." />
      </div>
      <CheckboxField label="Default sender id for the tenant" checked={isDefault} onChange={setIsDefault} />
      <TextAreaField label="Documents (JSON, optional)" value={documents} onChange={setDocuments} mono rows={3}
        error={docs.ok ? null : docs.error} />
      <MutationError error={create.error} service="messaging" />
    </Modal>
  );
}
